// =============================================================================
// GabineteView.ts — Gabinete de Campanha (#94): loja de upgrades PERMANENTES
// pagos com Caixa 2, mais o painel do Curral Eleitoral (#90) com os votos
// vitalícios e o progresso até o próximo desbloqueio. Toda compra é validada
// no servidor — aqui é só vitrine e clique.
// =============================================================================
import {
  META_UNLOCKS, META_UPGRADE_LIST, isCharUnlocked, isWeaponUnlocked,
  metaTotalSpent, metaUpgradeCost, nextUnlock,
  type MetaProfile, type MetaUpgradeDef,
} from '../../../shared/meta';
import { comprarUpgrade, fetchPerfil, reformaMinisterial } from '../../infrastructure/MetaClient';
import { audio } from '../AudioEngine';
import { $ } from './dom';
import { cadeadoIcon, caixa2Icon, gabineteIcon } from './pixelIcons';

const fmt = (n: number): string => n.toLocaleString('pt-BR');

// Desde a issue #122 o Gabinete vive como aba da tela CONQUISTAS E ARQUIVOS
export class GabineteView {
  private profile: MetaProfile | null = null;
  private persisted = false;   // false = perfil local do navegador (anônimo/dev)
  private busy = false;   // uma compra por vez: o caixa do comitê é singelo

  constructor(
    private readonly onProfile: (profile: MetaProfile) => void,  // avisa o app (cadeados do CharGrid)
  ) {
    $('btn-gab-respec').onclick = () => void this.respec();
  }

  show(): void {
    this.setMsg('');
    void this.load();
  }

  private async load(): Promise<void> {
    $('gab-lista').innerHTML = '<p class="zona-vazia">Consultando o caixa do comitê...</p>';
    const res = await fetchPerfil();
    if (!res.ok || !res.profile) {
      $('gab-lista').innerHTML =
        '<p class="zona-vazia">O caixa do comitê está inacessível. Tente de novo em instantes.</p>';
      return;
    }
    this.persisted = res.persisted;
    this.applyProfile(res.profile);
  }

  private applyProfile(profile: MetaProfile): void {
    this.profile = profile;
    this.onProfile(profile);
    this.renderSaldo();
    this.renderCurral();
    this.renderLoja();
  }

  // ---------------------------------------------------------------------------
  // Cabeçalho: saldo de Caixa 2 + votos vitalícios
  // ---------------------------------------------------------------------------
  private renderSaldo(): void {
    const p = this.profile!;
    $('gab-saldo').innerHTML =
      `${caixa2Icon(22)} <b>${fmt(p.caixa2)}</b> <span>de Caixa 2</span>`;
    $('gab-votos').innerHTML =
      `<b>${fmt(p.votos)}</b> <span>votos vitalícios</span>`;
    $('gab-aviso').textContent = this.persisted
      ? 'Perfil atrelado ao seu título de eleitor (conta Google).'
      : 'Perfil salvo neste navegador (sem login). Entre com Google pra levar o progresso pra qualquer dispositivo.';
  }

  // ---------------------------------------------------------------------------
  // Curral Eleitoral (#90): progresso até o próximo desbloqueio + a lista toda
  // ---------------------------------------------------------------------------
  private renderCurral(): void {
    const p = this.profile!;
    const box = $('gab-curral');
    const prox = nextUnlock(p.votos);
    let html = '';
    if (prox) {
      const pct = Math.min(100, (p.votos / prox.votos) * 100);
      html += `
        <div class="gab-meta-prox">
          <span>PRÓXIMO DESBLOQUEIO: <b>${prox.nome}</b></span>
          <span class="num">${fmt(p.votos)} / ${fmt(prox.votos)}</span>
        </div>
        <div class="gab-barra"><div style="width:${pct}%"></div></div>`;
    } else {
      html += '<div class="gab-meta-prox"><span>CURRAL COMPLETO: todo o sigilo foi derrubado.</span></div>';
    }
    html += '<div class="gab-unlocks">';
    for (const u of META_UNLOCKS) {
      const aberto = u.kind === 'character'
        ? isCharUnlocked(p.votos, u.id)
        : isWeaponUnlocked(p.votos, u.id);
      html += `
        <span class="gab-unlock${aberto ? ' aberto' : ''}">
          ${aberto ? '' : cadeadoIcon(13)}
          ${u.nome} <small>${aberto ? 'DESBLOQUEADO' : `${fmt(u.votos)} votos`}</small>
        </span>`;
    }
    html += '</div>';
    box.innerHTML = html;
  }

  // ---------------------------------------------------------------------------
  // Loja: os 6 upgrades permanentes
  // ---------------------------------------------------------------------------
  private renderLoja(): void {
    const p = this.profile!;
    const box = $('gab-lista');
    box.innerHTML = '';
    for (const def of META_UPGRADE_LIST) {
      box.appendChild(this.buildItem(def, p));
    }
    const gasto = metaTotalSpent(p.upgrades);
    const respec = $('btn-gab-respec') as HTMLButtonElement;
    respec.disabled = gasto <= 0 || this.busy;
    $('gab-respec-info').textContent = gasto > 0
      ? `Devolve ${fmt(gasto)} de Caixa 2 e zera todos os niveis.`
      : 'Nada comprado ainda — nada a reformar.';
  }

  private buildItem(def: MetaUpgradeDef, p: MetaProfile): HTMLElement {
    const lvl = Math.min(p.upgrades[def.id] ?? 0, def.max);
    const maxed = lvl >= def.max;
    const custo = maxed ? 0 : metaUpgradeCost(def, lvl);
    const card = document.createElement('div');
    card.className = `gab-item${maxed ? ' maxed' : ''}`;
    const pips = Array.from({ length: def.max },
      (_, i) => `<i class="${i < lvl ? 'on' : ''}"></i>`).join('');
    card.innerHTML = `
      <span class="gab-item-icone">${gabineteIcon(def.id, 30)}</span>
      <div class="gab-item-info">
        <div class="gab-item-nome">${def.nome} <span class="gab-pips">${pips}</span></div>
        <p class="gab-item-desc">${def.desc}</p>
      </div>
      <div class="gab-item-compra">
        ${maxed
    ? '<span class="gab-max">MÁXIMO</span>'
    : `<span class="gab-preco">${caixa2Icon(14)} ${fmt(custo)}</span>`}
      </div>`;
    if (!maxed) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn-pixel btn-verde gab-comprar';
      btn.textContent = 'COMPRAR';
      btn.disabled = this.busy || p.caixa2 < custo;
      btn.onclick = () => void this.buy(def.id);
      card.querySelector('.gab-item-compra')!.appendChild(btn);
    }
    return card;
  }

  // ---------------------------------------------------------------------------
  // Ações (o servidor é quem manda; a resposta traz o perfil novo)
  // ---------------------------------------------------------------------------
  private async buy(itemId: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setMsg('');
    audio.sfx.urnaKey();
    const res = await comprarUpgrade(this.persisted, itemId);
    this.busy = false;
    if (!res.ok || !res.profile) {
      this.setMsg(res.error ?? 'A compra emperrou no protocolo.');
      audio.sfx.casinoLose();
      if (res.profile) this.applyProfile(res.profile);
      return;
    }
    audio.sfx.casinoWin();
    this.applyProfile(res.profile);
  }

  private async respec(): Promise<void> {
    if (this.busy || !this.profile) return;
    this.busy = true;
    this.setMsg('');
    audio.sfx.urnaKey();
    const res = await reformaMinisterial(this.persisted);
    this.busy = false;
    if (!res.ok || !res.profile) {
      this.setMsg(res.error ?? 'A Reforma Ministerial emperrou no plenário.');
      return;
    }
    this.setMsg('Reforma Ministerial concluída: verba devolvida ao Caixa 2.');
    this.applyProfile(res.profile);
  }

  private setMsg(msg: string): void {
    $('gab-msg').textContent = msg;
  }
}
